import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  MANDATE_CANCELLATION_STATUSES,
  PERMISSIONS,
  type MandateCancellationStatus,
} from '@solar/contracts';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { FormField } from '../../../components/forms/FormField';
import { PageSpinner } from '../../../components/PageSpinner';
import { StatusBadge } from '../../../components/StatusBadge';
import { useAuth } from '../../../features/auth/auth-context';
import { mandatesApi } from '../../../features/mandates/mandates.api';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatStatusLabel } from '../../../lib/format-status';
import { paths, routeTo } from '../../../routes/paths';
import styles from './AdminPortal.module.css';

function formatTimestamp(value: string | null): string {
  return value === null ? '—' : new Date(value).toLocaleString();
}

export function AdminMandateCancellationsPage(): React.JSX.Element {
  const { id } = useParams<{ id?: string }>();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [statusFilter, setStatusFilter] = useState<MandateCancellationStatus | ''>('');
  const [notes, setNotes] = useState('');
  const [providerReference, setProviderReference] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const list = useQuery({
    queryKey: ['admin', 'mandate-cancellations', statusFilter],
    queryFn: () =>
      mandatesApi.listCancellationRequests({
        pageSize: 50,
        ...(statusFilter === '' ? {} : { status: statusFilter }),
      }),
    enabled: id === undefined,
  });

  const detail = useQuery({
    queryKey: ['admin', 'mandate-cancellations', id],
    queryFn: () => mandatesApi.getCancellationRequest(id ?? ''),
    enabled: id !== undefined,
  });

  const invalidateDetail = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'mandate-cancellations'] });
    if (id !== undefined) {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'mandate-cancellations', id] });
    }
  };

  const review = useMutation({
    mutationFn: () =>
      mandatesApi.reviewCancellationRequest(id ?? '', { notes: notes || undefined }),
    onSuccess: invalidateDetail,
  });

  const submitToProvider = useMutation({
    mutationFn: () =>
      mandatesApi.submitCancellationToProvider(id ?? '', {
        providerSupportReference: providerReference || undefined,
        notes: notes || undefined,
      }),
    onSuccess: invalidateDetail,
  });

  const confirm = useMutation({
    mutationFn: () => mandatesApi.confirmCancellation(id ?? '', { notes: notes || undefined }),
    onSuccess: invalidateDetail,
  });

  const reject = useMutation({
    mutationFn: () =>
      mandatesApi.rejectCancellation(id ?? '', {
        rejectionReason,
        notes: notes || undefined,
      }),
    onSuccess: invalidateDetail,
  });

  const canManage = can(PERMISSIONS.MANDATES_CANCEL_MANAGE);

  if (id !== undefined) {
    if (detail.isPending) {
      return <PageSpinner label="Loading cancellation request" />;
    }

    if (detail.isError) {
      return <Alert tone="error">{toDisplayMessage(detail.error)}</Alert>;
    }

    const data = detail.data;
    const mutationError =
      review.error ?? submitToProvider.error ?? confirm.error ?? reject.error ?? null;
    const mutationSuccess =
      review.isSuccess || submitToProvider.isSuccess || confirm.isSuccess || reject.isSuccess;

    return (
      <div className={styles.page}>
        <header className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Cancellation request</h1>
          <StatusBadge status={data.status} />
        </header>

        <p>
          <Link to={paths.adminMandateCancellations}>Back to queue</Link>
        </p>

        {mutationError !== null && <Alert tone="error">{toDisplayMessage(mutationError)}</Alert>}
        {mutationSuccess && <Alert tone="success">Cancellation request updated.</Alert>}

        <section className={styles.card} aria-labelledby="cancellation-detail-heading">
          <h2 className={styles.cardTitle} id="cancellation-detail-heading">
            Request details
          </h2>

          <dl className={styles.definitionList}>
            <dt>Mandate</dt>
            <dd>
              <Link to={routeTo.adminMandate(data.mandateId)}>{data.mandateId.slice(0, 8)}</Link>
            </dd>
            <dt>Reason</dt>
            <dd>{data.requestReason}</dd>
            <dt>Requested</dt>
            <dd>{formatTimestamp(data.requestedAt)}</dd>
            <dt>Submitted to provider</dt>
            <dd>{formatTimestamp(data.submittedToProviderAt)}</dd>
            <dt>Provider reference</dt>
            <dd>{data.providerSupportReference ?? '—'}</dd>
            <dt>Confirmed</dt>
            <dd>{formatTimestamp(data.confirmedAt)}</dd>
            <dt>Rejected</dt>
            <dd>{formatTimestamp(data.rejectedAt)}</dd>
            <dt>Rejection reason</dt>
            <dd>{data.rejectionReason ?? '—'}</dd>
            <dt>Notes</dt>
            <dd>{data.notes ?? '—'}</dd>
          </dl>
        </section>

        {canManage && (
          <section className={styles.card} aria-labelledby="cancellation-actions-heading">
            <h2 className={styles.cardTitle} id="cancellation-actions-heading">
              Staff actions
            </h2>

            <FormField
              label="Notes"
              name="notes"
              value={notes}
              onChange={(event) => {
                setNotes(event.target.value);
              }}
            />

            {data.status === 'REQUESTED' && (
              <div className={styles.actions}>
                <Button disabled={review.isPending} onClick={() => review.mutate()}>
                  Mark under review
                </Button>
              </div>
            )}

            {(data.status === 'REQUESTED' || data.status === 'UNDER_REVIEW') && (
              <>
                <FormField
                  label="Provider support reference"
                  name="providerReference"
                  value={providerReference}
                  onChange={(event) => {
                    setProviderReference(event.target.value);
                  }}
                />
                <div className={styles.actions}>
                  <Button
                    disabled={submitToProvider.isPending}
                    onClick={() => submitToProvider.mutate()}
                  >
                    Submit to provider
                  </Button>
                </div>
              </>
            )}

            {data.status === 'SUBMITTED_TO_PROVIDER' && (
              <div className={styles.actions}>
                <Button disabled={confirm.isPending} onClick={() => confirm.mutate()}>
                  Confirm cancellation
                </Button>
              </div>
            )}

            {data.status !== 'CONFIRMED' &&
              data.status !== 'REJECTED' &&
              data.status !== 'CANCELLED' && (
                <>
                  <FormField
                    label="Rejection reason"
                    name="rejectionReason"
                    value={rejectionReason}
                    required
                    onChange={(event) => {
                      setRejectionReason(event.target.value);
                    }}
                  />
                  <div className={styles.actions}>
                    <Button
                      variant="secondary"
                      disabled={reject.isPending || rejectionReason.trim() === ''}
                      onClick={() => reject.mutate()}
                    >
                      Reject request
                    </Button>
                  </div>
                </>
              )}
          </section>
        )}
      </div>
    );
  }

  const items = list.data?.items ?? [];

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Mandate cancellations</h1>
        <p className={styles.pageSubtitle}>
          Manual provider cancellation requests awaiting staff action.
        </p>
      </header>

      <section className={styles.card} aria-labelledby="cancellation-queue-heading">
        <h2 className={styles.cardTitle} id="cancellation-queue-heading">
          Queue
        </h2>

        <form className={styles.filters} aria-label="Filter cancellation requests">
          <label htmlFor="cancellationStatusFilter">
            Status
            <select
              id="cancellationStatusFilter"
              name="cancellationStatusFilter"
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value as MandateCancellationStatus | '');
              }}
            >
              <option value="">All statuses</option>
              {MANDATE_CANCELLATION_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {formatStatusLabel(status)}
                </option>
              ))}
            </select>
          </label>
        </form>

        {list.isPending && <PageSpinner label="Loading cancellation requests" />}
        {list.isError && <Alert tone="error">{toDisplayMessage(list.error)}</Alert>}

        {list.isSuccess && items.length === 0 && (
          <p className={styles.empty}>
            No mandate cancellations need review. You're all caught up.
          </p>
        )}

        {list.isSuccess && items.length > 0 && (
          <div className={styles.tableWrapper}>
            <table className={styles.table} aria-label="Cancellation requests">
              <thead>
                <tr>
                  <th scope="col">Requested</th>
                  <th scope="col">Mandate</th>
                  <th scope="col">Status</th>
                  <th scope="col">Reason</th>
                </tr>
              </thead>
              <tbody>
                {items.map((request) => (
                  <tr key={request.id}>
                    <td>
                      <Link to={routeTo.adminMandateCancellation(request.id)}>
                        {formatTimestamp(request.requestedAt)}
                      </Link>
                    </td>
                    <td>{request.mandateId.slice(0, 8)}</td>
                    <td>
                      <StatusBadge status={request.status} />
                    </td>
                    <td>{request.requestReason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
